'use client';

import { useState, useRef, useEffect } from 'react';
import { UseFormRegisterReturn, FieldValues, Path } from 'react-hook-form';

import { PhoneInputProps, CountryOption } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

export const PhoneInput = <T extends FieldValues>({
  label,
  error,
  watch,
  onPhoneChange,
  className,
  countries,
  ...inputProps
}: PhoneInputProps<T> & UseFormRegisterReturn) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(
    countries[0],
  );

  const value = watch(inputProps.name as Path<T>) || '';
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isDropdownOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    if (inputValue.length <= selectedCountry.code.length) {
      inputValue = selectedCountry.code;
    }

    if (!inputValue.startsWith(selectedCountry.code)) {
      inputValue = selectedCountry.code + inputValue.replace(/\D/g, '');
    } else {
      const prefix = selectedCountry.code;
      const afterPrefix = inputValue
        .substring(prefix.length)
        .replace(/\D/g, '');

      inputValue = prefix + afterPrefix;
    }

    if (inputProps.onChange) {
      const event = {
        ...e,
        target: {
          ...e.target,
          value: inputValue,
        },
      };
      inputProps.onChange(event);
    }

    if (onPhoneChange) {
      onPhoneChange(inputValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);

    if (!value) {
      if (onPhoneChange) {
        onPhoneChange(selectedCountry.code);
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);

    if (value === selectedCountry.code) {
      if (onPhoneChange) {
        onPhoneChange('');
      }
    }
  };

  const selectCountry = (country: CountryOption) => {
    const prevCountry = selectedCountry;
    setSelectedCountry(country);
    setIsDropdownOpen(false);

    if (onPhoneChange && value) {
      if (value.startsWith(prevCountry.code)) {
        const numberPart = value.substring(prevCountry.code.length);
        onPhoneChange(country.code + numberPart);
      } else {
        onPhoneChange(country.code + value.replace(/\D/g, ''));
      }
    } else if (isFocused && onPhoneChange) {
      onPhoneChange(country.code);
    }
  };

  return (
    <div ref={dropdownRef} className={classNames('relative', {}, [className])}>
      <div className="relative h-14 max-sm:h-12">
        <div className="relative flex h-full w-full">
          <div
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="absolute bottom-0 left-0 top-0 z-10 flex cursor-pointer items-center justify-center pl-3 pr-1"
          >
            <div className="relative flex items-center">
              <div className="flex h-4 w-6 items-center justify-center">
                <img
                  src={selectedCountry.flag}
                  alt={selectedCountry.label}
                  className="user-select-none h-full w-full rounded-[3px]"
                />
              </div>

              <IconChevron
                className={classNames('transition-transform', {
                  'rotate-180': isDropdownOpen,
                })}
              />
            </div>
          </div>

          <input
            {...inputProps}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            id={label}
            value={value}
            className={classNames(
              'transition-bg peer h-full w-full rounded-xl border border-transparent bg-customSurfaceContrastLow px-3 py-2 pl-16 pt-6 text-base outline-none duration-300',
              {
                '!border-statusErrorSecondary bg-statusErrorTertiary': !!error,
                'hover:bg-surfaceBaseInverseTertiary focus:bg-surfaceBaseInverseTertiary':
                  !error,
              },
            )}
          />

          <label
            htmlFor={label}
            className={classNames(
              'pointer-events-none absolute left-16 top-1/2 -translate-y-1/2 cursor-text text-base tracking-[-0.01rem] text-textBaseSecondary transition-all duration-300 peer-focus:top-4',
              {
                'top-4 text-sm leading-[18px]': isFocused || value,
              },
            )}
          >
            {label}
          </label>
        </div>
      </div>

      {!!error?.message && (
        <span className="mt-1 block text-[12px] font-normal leading-[18px] text-statusErrorSecondary">
          {error?.message}
        </span>
      )}

      {isDropdownOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-xl bg-surfaceBaseInversePrimary2 p-[6px]">
          <ul className="custom-scrollbar max-h-[250px] w-full overflow-y-auto rounded-xl bg-customSurfaceContrastLow py-2">
            {countries.map((country) => (
              <li
                onClick={() => selectCountry(country)}
                className="flex min-h-[40px] cursor-pointer items-center px-3 text-[16px] text-surfaceBasePrimary hover:bg-surfaceBaseInverseSecondary"
                key={country.value}
              >
                <div className="mr-2 flex h-4 w-6 items-center justify-center">
                  <img
                    src={country.flag}
                    alt={country.label}
                    className="user-select-none h-full w-full rounded-[3px]"
                  />
                </div>
                <span>{country.label}</span>
                <span className="ml-auto text-textBaseInverseTertiary">
                  {country.code}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
